import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router";
import {
  ArrowRight,
  Filter,
  Search,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import { INDUSTRIES, getItemsByIndustry } from "../../data/categoryData";
import { useCategories } from "../../contexts/CategoriesContext";

const DEFAULT_SUGGESTIONS = [
  "land registration",
  "personal injury",
  "meal plans",
  "career change",
  "mortgage",
  "betting tips",
];

function pickMenuSlug(entry) {
  if (!entry?.slug) return "";
  return String(entry.slug).replace(/^\//, "").split("/").filter(Boolean).pop() || "";
}

/**
 * Vertical smart search: industry filter + query, suggestions, then results.
 */
const SmartTopicsSection = ({
  suggestedQueries = DEFAULT_SUGGESTIONS,
  defaultVisibleCount = 12,
  categoriesHref = "/#categories",
  className = "",
}) => {
  const { industries: apiIndustries, menuByIndustry } = useCategories();
  const [query, setQuery] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [searchFocused, setSearchFocused] = useState(false);

  const industries = apiIndustries.length ? apiIndustries : INDUSTRIES;

  const allTopics = useMemo(() => {
    const fromApi = industries.flatMap((industry) => {
      const menu = menuByIndustry[industry.id] ?? [];
      if (menu.length) {
        return menu.map((item) => ({
          id: `${industry.id}-${item.id || item.slug || item.label}`,
          name: item.label || item.name,
          description: item.description || "",
          slug: pickMenuSlug(item) || item.slug,
          industry,
        }));
      }
      return getItemsByIndustry(industry.id).map((item) => ({
        id: `${industry.id}-${item.slug}`,
        name: item.name,
        description: item.description || "",
        slug: item.slug,
        industry,
      }));
    });
    return fromApi.filter((t) => t.slug && t.name);
  }, [industries, menuByIndustry]);

  const filteredTopics = useMemo(() => {
    let list = [...allTopics];

    if (industryFilter !== "all") {
      list = list.filter((topic) => topic.industry.id === industryFilter);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((topic) =>
        `${topic.name} ${topic.description} ${topic.industry.label}`.toLowerCase().includes(q),
      );
    } else {
      list = list.slice(0, defaultVisibleCount);
    }

    return list;
  }, [allTopics, industryFilter, query, defaultVisibleCount]);

  const isSearching = Boolean(query.trim()) || industryFilter !== "all";
  const clearSearch = () => {
    setQuery("");
    setIndustryFilter("all");
  };

  return (
    <section
      className={`relative overflow-hidden bg-[#f7f4ec] px-4 py-20 sm:px-6 lg:px-8 ${className}`.trim()}
      aria-labelledby="smart-topics-heading"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-white to-transparent via-white/80" />
      <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-[#FFD221]/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-10 h-64 w-64 rounded-full bg-[#c9a000]/12 blur-3xl" />

      <div className="relative mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center sm:mb-12"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-black shadow-sm">
            <Search size={12} strokeWidth={2.5} />
            Smart topic finder
          </span>
          <h2 id="smart-topics-heading" className="mt-5 text-3xl font-black leading-tight text-black sm:text-4xl lg:text-5xl">
            Find the right guide in seconds
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">
            Filter by category, type your question, and jump straight into a topic mini-site with articles, media, and
            provider guidance.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="overflow-hidden rounded-[2rem] border border-black/[0.08] bg-white shadow-[0_30px_80px_-45px_rgba(0,0,0,0.45)] ring-1 ring-black/[0.04]"
        >
          <div className="border-b border-black/[0.06] bg-gradient-to-r from-[#f7f4ec] via-white to-[#FFD221]/10 p-5 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
              <div className="relative shrink-0 sm:w-52 lg:w-56">
                <label htmlFor="topic-industry-filter" className="sr-only">
                  Filter by category
                </label>
                <Filter
                  size={16}
                  className="pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <select
                  id="topic-industry-filter"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  className="h-full w-full appearance-none rounded-2xl border border-black/10 bg-white py-3.5 pl-10 pr-9 text-sm font-bold text-gray-800 outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/30"
                >
                  <option value="all">All categories</option>
                  {industries.map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.label}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className={`relative min-w-0 flex-1 rounded-2xl border bg-white transition-[box-shadow,border-color] ${
                  searchFocused
                    ? "border-black/25 shadow-[0_0_0_3px_rgba(255,210,33,0.35)]"
                    : "border-black/10 shadow-sm"
                }`}
              >
                <Search
                  className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${searchFocused ? "text-[#c9a000]" : "text-gray-400"}`}
                  size={18}
                  aria-hidden
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Try: recover money owed, land verification, injury claim…"
                  className="w-full rounded-2xl bg-transparent py-3.5 pl-11 pr-11 text-sm font-medium text-black outline-none placeholder:text-gray-400"
                  aria-label="Search topics"
                />
                <AnimatePresence>
                  {query ? (
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-black/[0.06] hover:text-black"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                    >
                      <X size={16} />
                    </motion.button>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="border-b border-black/[0.05] px-5 py-4 sm:px-6">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Suggested searches</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestedQueries.map((s) => {
                const active = query.toLowerCase() === s.toLowerCase();
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setQuery((prev) => (prev.toLowerCase() === s.toLowerCase() ? "" : s))}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-black transition ${
                      active
                        ? "bg-black text-[#FFD221] shadow-md"
                        : "bg-[#f7f4ec] text-black ring-1 ring-black/10 hover:ring-black/25"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-xs font-bold text-gray-600">
                <TrendingUp size={14} className="text-[#c9a000]" aria-hidden />
                {isSearching ? (
                  <>
                    <span className="tabular-nums text-gray-900">{filteredTopics.length}</span>
                    {filteredTopics.length === 1 ? " topic" : " topics"} found
                  </>
                ) : (
                  <>Top {filteredTopics.length} topics to explore</>
                )}
              </p>
              {isSearching ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-xs font-black text-[#c9a000] underline-offset-2 hover:underline"
                >
                  Clear filters
                </button>
              ) : (
                <Link
                  to={categoriesHref}
                  className="inline-flex items-center gap-1 text-xs font-black text-black hover:text-[#c9a000]"
                >
                  Browse all
                  <ArrowRight size={12} />
                </Link>
              )}
            </div>

            <AnimatePresence mode="wait">
              {filteredTopics.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/15 bg-[#f7f4ec]/60 px-6 py-14 text-center"
                >
                  <Search className="mb-3 text-gray-300" size={36} strokeWidth={1.5} />
                  <p className="text-sm font-black text-gray-800">No topics match yet</p>
                  <p className="mt-2 max-w-sm text-xs text-gray-500">
                    Try another keyword or switch the category filter.
                  </p>
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="mt-5 rounded-xl bg-[#FFD221] px-5 py-2.5 text-xs font-black text-black"
                  >
                    Reset search
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  {filteredTopics.map((topic) => (
                    <motion.div
                      key={topic.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={`/${topic.industry.id}/${topic.slug}`}
                        className="group flex h-full flex-col rounded-2xl border border-black/10 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#FFD221]/50 hover:shadow-lg"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                            {topic.industry.label}
                          </span>
                          <Sparkles size={14} className="shrink-0 text-[#FFD221] opacity-0 transition group-hover:opacity-100" />
                        </div>
                        <h3 className="mt-2 text-base font-black leading-snug text-black">{topic.name}</h3>
                        <p className="mt-1.5 line-clamp-2 flex-1 text-xs leading-relaxed text-gray-500">
                          {topic.description}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1 text-xs font-black text-black">
                          Open topic
                          <ArrowRight size={12} className="transition group-hover:translate-x-1" />
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SmartTopicsSection;
